import { gsap } from "gsap";

/**
 * Hero block animation — VERSIONE CON LOG DI DEBUG.
 * Rimuovere i console.log prima del deploy.
 *
 * NB: l'evento "intro-complete" viene dispatchato dall'intro variante "soft"
 * con un anticipo di ~2.2s rispetto alla fine effettiva dello slide-up del
 * pannello. Per evitare che logo/testi/CTA appaiano DURANTE l'uscita del
 * pannello bianco (sovrapposti graficamente), li ritardiamo della stessa
 * durata. L'immagine di sfondo invece parte subito così l'utente la vede
 * emergere mentre il pannello sale.
 */

// Durata dello slide-up del pannello intro: i contenuti hero (logo/testi/CTA)
// vengono ritardati di questa quantità così appaiono SOLO a intro finita.
// Per la variante classic dell'intro questo offset è "innocuo" (vedi più
// in basso: bg parte subito, contenuti hero a +2.2s funziona comunque).
const INTRO_PANEL_EXIT_DURATION = 2.2;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Setup video di sfondo (lazy load + autoplay con fallback).
 *
 * I 2 <video> nel markup hanno data-src ma niente src — JS carica solo il
 * video pertinente al viewport corrente per non sprecare banda. In caso di
 * autoplay bloccato (es. iOS Low Power Mode) il poster (immagine_sfondo)
 * resta visibile come fallback.
 *
 * Restituisce l'elemento "media" attivo (video o img) su cui applicare
 * l'animazione filter+scale, oppure null se reduced-motion (in quel caso
 * useremo solo img).
 */
const setupVideoBg = (bg) => {
  if (!bg || !bg.classList.contains("hero__bg--has-video")) return null;

  // Reduced motion: niente video, solo l'immagine poster
  if (prefersReducedMotion()) return null;

  const isMobile = window.matchMedia("(max-width: 991.98px)").matches;
  const activeVideo = bg.querySelector(
    isMobile ? "[data-hero-video='mobile']" : "[data-hero-video='desktop']"
  );
  if (!activeVideo) return null;

  const src = activeVideo.dataset.src;
  if (!src) return null;

  // Lazy load: imposta src ora (il browser inizia il download)
  activeVideo.src = src;

  // Aggiungi classe sul wrapper quando il video sta giocando
  // (nasconde l'immagine poster con fade)
  const onPlaying = () => {
    bg.classList.add("hero__bg--video-playing");
  };
  activeVideo.addEventListener("playing", onPlaying, { once: true });

  // Tentativo di play (alcuni browser richiedono chiamata esplicita)
  const playPromise = activeVideo.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {
      // Autoplay bloccato (iOS Low Power, policy browser):
      // il poster resta visibile, nessuna classe --video-playing.
      // Nessun fallback estremo richiesto.
    });
  }

  return activeVideo;
};

const animateHero = (hero) => {
  console.log("animateHero chiamato per:", hero);

  const bg = hero.querySelector('[data-reveal="hero-bg"]');
  const logo = hero.querySelector('[data-reveal="hero-logo"]');
  const t1 = hero.querySelector('[data-reveal="hero-t1"]');
  const t2 = hero.querySelector('[data-reveal="hero-t2"]');
  const desc = hero.querySelector('[data-reveal="hero-desc"]');
  const cta = hero.querySelector('[data-reveal="hero-cta"]');

  // Setup video se presente; ritorna il <video> attivo o null
  const activeVideo = setupVideoBg(bg);

  // L'elemento "media" da animare: video se presente e attivo, altrimenti img
  const bgMedia = activeVideo || (bg ? bg.querySelector("img") : null);

  if (prefersReducedMotion()) {
    gsap.set([bg, logo, t1, t2, desc, cta].filter(Boolean), {
      opacity: 1,
      y: 0,
    });
    if (bg) {
      gsap.set(bg, { "--hero-grayscale": 0, "--hero-contrast": 1 });
    }
    return;
  }

  const tl = gsap.timeline();

  if (bg) {
    tl.fromTo(bg,
      { opacity: 0 },
      { opacity: 1, duration: 0.3, ease: "power2.out" },
      0
    );

    tl.fromTo(bg,
      { "--hero-grayscale": 1, "--hero-contrast": 1 },
      { "--hero-grayscale": 0, "--hero-contrast": 1, duration: 5, ease: "power2.inOut" },
      0
    );

    // Anima scale+yPercent sull'elemento media (video o img, indistintamente)
    if (bgMedia) {
      tl.fromTo(bgMedia,
        { scale: 1.08, yPercent: 1 },
        { scale: 1, yPercent: 0, duration: 5, ease: "none" },
        0
      );
    }
  }

  if (logo) {
    tl.fromTo(logo,
      { opacity: 0, scale: 0.85 },
      { opacity: 1, scale: 1, duration: 0.7, ease: "power2.out" },
      INTRO_PANEL_EXIT_DURATION + 0.52
    );
  }

  if (t1) {
    tl.fromTo(t1,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" },
      INTRO_PANEL_EXIT_DURATION + 0.5
    );
  }

  if (t2) {
    tl.fromTo(t2,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" },
      INTRO_PANEL_EXIT_DURATION + 0.8
    );
  }

  if (desc) {
    tl.fromTo(desc,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
      INTRO_PANEL_EXIT_DURATION + 1.3
    );
  }

  if (cta) {
    tl.fromTo(cta,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
      INTRO_PANEL_EXIT_DURATION + 1.6
    );
  }

};

const startHero = (hero) => {
  const hasOverlay = !!document.querySelector("[data-intro-overlay]");

  if (hasOverlay) {
    window.addEventListener("intro-complete", () => {
      animateHero(hero);
    }, { once: true });
  } else {
    animateHero(hero);
  }
};

const observeHero = (hero) => {
  console.log("observeHero per:", hero);
  hero.classList.add("is-animatable");

  const observer = new IntersectionObserver(
    ([entry], obs) => {
      console.log("OBSERVER CALLBACK scattato, isIntersecting:", entry.isIntersecting);
      if (entry.isIntersecting) {
        obs.disconnect();
        startHero(hero);
      }
    },
    { threshold: 0.15 }
  );

  observer.observe(hero);
  console.log("observer registrato per:", hero);
};

const initHero = () => {
  console.log("initHero chiamata");
  const heroes = document.querySelectorAll(".blocco_hero");
  console.log("heroes trovati:", heroes.length);
  if (!heroes.length) return;
  heroes.forEach(observeHero);
};

export default () => {
  console.log("hero default export chiamato, readyState:", document.readyState);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      console.log("DOMContentLoaded scattato");
      initHero();
    });
    console.log("hero1 - in attesa di DOMContentLoaded");
  } else {
    console.log("hero2 - vado diretto");
    initHero();
  }
};